import { z } from 'zod';
import { i18nTextSchema } from '../i18nText.js';
import { isIsoDate } from './dates.js';
// Tarifes del lloguer (§6, `properties/{id}/pricing/main`). Tots els imports en CÈNTIMS (enters).
export const centsSchema = z.number().int().min(0).max(1_000_000_00);
export const pctSchema = z.number().min(0).max(100);
export const isoDateSchema = z.string().refine(isIsoDate, 'invalid date');
/** Percentatge o import fix. */
export const pctOrAmountSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('pct'), value: pctSchema }),
    z.object({ type: z.literal('amount'), cents: centsSchema }),
]);
/** Percentatge o import per nit. */
export const pctOrPerNightSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('pct'), value: pctSchema }),
    z.object({ type: z.literal('perNight'), cents: centsSchema }),
]);
/** §6.3: descompte setmanal o mensual. `perUnit`: per cada setmana o mes sencer; `minDuration`: si l'estada en dura almenys un. */
export const periodDiscountSchema = z.object({
    mode: z.enum(['perUnit', 'minDuration']),
    amount: pctOrAmountSchema,
});
export const specialPeriodSchema = z
    .object({
    /** Primera nit del període (inclosa). */
    from: isoDateSchema,
    /** Dia següent a l'última nit (com un `checkOut`). */
    to: isoDateSchema,
    nightlyCents: centsSchema,
    label: i18nTextSchema.optional(),
})
    .refine((p) => p.to > p.from, { message: 'periodOrder', path: ['to'] });
export const extraUnitSchema = z.enum(['stay', 'night', 'week', 'month', 'year', 'service', 'metered']);
/**
 * Catàleg inicial d'extres (§6.4). Dos grups:
 * - `service`: serveis i subministraments. Els modes es llegeixen «inclòs / no inclòs / inclòs fins a».
 * - `propertyCost`: despeses de l'immoble. Els mateixos modes es llegeixen «a càrrec del propietari»
 *   (`included`) o «a càrrec de l'arrendatari» (`notIncluded`, amb import).
 * `defaultUnit` és el que es proposa en afegir-lo; l'agent el pot canviar.
 */
export const EXTRA_CATALOG = [
    { key: 'water', group: 'service', defaultUnit: 'month' },
    { key: 'electricity', group: 'service', defaultUnit: 'metered' },
    { key: 'internet', group: 'service', defaultUnit: 'month' },
    { key: 'cleaning', group: 'service', defaultUnit: 'service' },
    { key: 'finalCleaning', group: 'service', defaultUnit: 'stay' },
    { key: 'garden', group: 'service', defaultUnit: 'month' },
    { key: 'pool', group: 'service', defaultUnit: 'month' },
    { key: 'bedLinen', group: 'service', defaultUnit: 'service' },
    { key: 'bathLinen', group: 'service', defaultUnit: 'service' },
    { key: 'propertyTax', group: 'propertyCost', defaultUnit: 'year' },
    { key: 'wasteSewage', group: 'propertyCost', defaultUnit: 'year' },
    { key: 'homeInsurance', group: 'propertyCost', defaultUnit: 'year' },
    { key: 'communityFees', group: 'propertyCost', defaultUnit: 'month' },
];
export const EXTRA_KEYS = EXTRA_CATALOG.map((e) => e.key);
/** Grup d'un extra: els que no són al catàleg (propis de la propietat) es tracten com a servei. */
export function extraGroup(key) {
    return EXTRA_CATALOG.find((e) => e.key === key)?.group ?? 'service';
}
export const extraSchema = z.object({
    /** Clau del catàleg (§6.4) o una de pròpia de la propietat. */
    key: z.string().min(1).max(40),
    label: i18nTextSchema.optional(),
    mode: z.enum(['included', 'notIncluded', 'includedUpTo']),
    /** `notIncluded`: preu i unitat. */
    price: z.object({
        cents: centsSchema,
        unit: extraUnitSchema,
        /** `service`: cada quantes nits (7 setmanal, 14 quinzenal). */
        periodNights: z.number().int().min(1).max(365).optional(),
        /** `metered`: unitat de consum, p. ex. «kWh». */
        meteredUnit: z.string().max(10).optional(),
    }).optional(),
    /** `includedUpTo`: màxim mensual en cèntims, prorratejat per les nits de cada mes. */
    capCentsPerMonth: centsSchema.optional(),
    /** IVA de l'extra, si el lloguer el contempla (§6.5). */
    vatPct: pctSchema.optional(),
});
export const cancellationTierSchema = z.object({
    /** Es retorna `refundPct` si es cancel·la almenys aquests dies abans de l'entrada. */
    minDaysBefore: z.number().int().min(0).max(730),
    refundPct: pctSchema,
});
export const presentationSchema = z.object({
    granularity: z.enum(['night', 'week', 'month', 'total']),
    extras: z.enum(['spread', 'itemized']),
    detail: z.enum(['breakdown', 'aggregate']),
});
export const pricingConfigSchema = z.object({
    basis: z.enum(['monthly', 'daily']),
    /** `monthly`: preu de cada mes, de gener a desembre. */
    monthPrices: z.array(centsSchema).length(12),
    /** `daily`: preu per nit, i opcionalment un per a cada mes (null = el general). */
    dailyPrice: centsSchema,
    dailyByMonth: z.array(centsSchema.nullable()).length(12).nullable(),
    /** Per ordre: el primer que conté la nit és el que s'aplica. */
    specialPeriods: z.array(specialPeriodSchema).max(50),
    shortStaySurcharges: z.object({
        week: pctOrPerNightSchema.nullable(),
        fortnight: pctOrPerNightSchema.nullable(),
        month: pctOrPerNightSchema.nullable(),
        thresholds: z.object({
            week: z.number().int().min(1).max(366),
            fortnight: z.number().int().min(1).max(366),
            month: z.number().int().min(1).max(366),
        }),
    }),
    discounts: z.object({
        daily: pctOrPerNightSchema.nullable(),
        weekly: periodDiscountSchema.nullable(),
        monthly: periodDiscountSchema.nullable(),
        global: pctOrAmountSchema.nullable(),
    }),
    extras: z.array(extraSchema).max(40),
    touristTax: z.object({
        centsPerPersonNight: centsSchema,
        /** A partir d'aquesta nit s'aplica la reducció (p. ex. 9). null = sense reducció. */
        reductionFromNight: z.number().int().min(2).max(366).nullable(),
        reductionPct: pctSchema,
    }),
    reservation: z.object({
        amount: pctOrAmountSchema,
        balanceDueDaysBefore: z.number().int().min(0).max(365),
    }),
    deposit: z.object({
        kind: z.enum(['amount', 'months', 'pct']),
        /** `amount`: cèntims; `months`: mesos de lloguer (mitjana mensual de l'estada, §6.8); `pct`: % del total. */
        value: z.number().min(0).max(1_000_000_00),
        dueAt: z.enum(['balance', 'checkIn']),
        returnDaysAfter: z.number().int().min(0).max(365),
    }),
    cancellationPolicy: z.array(cancellationTierSchema).max(10),
    presentation: presentationSchema,
    rounding: z.enum(['cent', 'euro']),
});
export const quoteInputSchema = z.object({
    checkIn: isoDateSchema,
    checkOut: isoDateSchema,
    /** Dia en què es fa la reserva: determina el calendari de pagaments. */
    bookingDate: isoDateSchema,
    /** Persones subjectes a l'impost turístic. */
    taxablePersons: z.number().int().min(0).max(50),
    /** §6.5: l'agent decideix a cada lloguer si es contemplen els impostos. */
    includeTouristTax: z.boolean(),
    includeVat: z.boolean(),
});
/** Tarifes per defecte d'una propietat nova: tot a zero i sense recàrrecs ni descomptes. */
export const DEFAULT_PRICING = {
    basis: 'monthly',
    monthPrices: Array.from({ length: 12 }, () => 0),
    dailyPrice: 0,
    dailyByMonth: null,
    specialPeriods: [],
    shortStaySurcharges: { week: null, fortnight: null, month: null, thresholds: { week: 7, fortnight: 15, month: 30 } },
    discounts: { daily: null, weekly: null, monthly: null, global: null },
    extras: [],
    touristTax: { centsPerPersonNight: 0, reductionFromNight: null, reductionPct: 0 },
    reservation: { amount: { type: 'pct', value: 30 }, balanceDueDaysBefore: 30 },
    deposit: { kind: 'amount', value: 0, dueAt: 'balance', returnDaysAfter: 15 },
    cancellationPolicy: [],
    presentation: { granularity: 'total', extras: 'itemized', detail: 'breakdown' },
    rounding: 'cent',
};
/** Ajustos d'un lloguer concret sobre les tarifes de la propietat (§6): substitueixen apartats sencers. */
export const pricingOverridesSchema = pricingConfigSchema.partial();
export function applyOverrides(config, overrides) {
    if (!overrides)
        return config;
    const merged = { ...config };
    // Només els apartats presents: un apartat absent conserva el valor de la propietat.
    for (const [key, value] of Object.entries(overrides))
        if (value !== undefined)
            merged[key] = value;
    return merged;
}
/** Entrada de la Function `savePricing`: les tarifes es desen sempre completes. */
export const savePricingInputSchema = z.object({
    propertyId: z.string().min(1).max(128),
    config: pricingConfigSchema,
});
